import { createORPCClient, onError } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import type { ApiRouterClient } from "@repo/api/orpc/router";
import { logOrpcClientError } from "@shared/lib/log-orpc-client-error";
import {
	formatOrpcProcedurePath,
	resolveOrpcPathFromRequestUrl,
} from "@shared/lib/orpc-request-path";
import {
	isLikelyNonOrpcHtmlResponseContentType,
	isOrpcCompatibleResponseContentType,
} from "@shared/lib/orpc-response-content-type";
import { resolveOrpcRpcUrl } from "@shared/lib/resolve-orpc-rpc-url";

const ANALYSE_INTERACTIONS_RPC_PATH = "/api/rpc/analyse/interactions/";

/** RPCLink may pass extra args; native `fetch` only uses (input, init). */
function browserFetch(
	input: RequestInfo | URL,
	init?: RequestInit,
	..._rest: unknown[]
): ReturnType<typeof fetch> {
	const requestUrl =
		typeof input === "string"
			? input
			: input instanceof URL
				? input.toString()
				: input.url;
	return globalThis
		.fetch(input, {
			...(init ?? {}),
			// Avoid stale GET responses (e.g. `/ai/chats/{id}`) after restart/reload.
			cache: "no-store",
			// Discovery interaction batches flush on `pagehide`; keepalive lets them outlive the page.
			keepalive: requestUrl.includes(ANALYSE_INTERACTIONS_RPC_PATH),
		})
		.then((response) => {
			if (process.env.NODE_ENV === "production") {
				return response;
			}
			if (
				requestUrl.includes("/api/rpc/") &&
				response.status !== 204 &&
				!isOrpcCompatibleResponseContentType(
					response.headers.get("content-type"),
				)
			) {
				const procedure =
					resolveOrpcPathFromRequestUrl(requestUrl) ?? "unknown";
				const contentType =
					response.headers.get("content-type") ?? "(missing)";
				const severity = isLikelyNonOrpcHtmlResponseContentType(
					contentType,
				)
					? "error"
					: "warn";
				const message = `[orpc:fetch] Unexpected RPC content-type procedure=${procedure} status=${response.status} contentType=${contentType} url=${requestUrl}`;
				if (severity === "error") {
					console.error(message);
				} else {
					console.warn(message);
				}
			}
			return response;
		});
}

const link = new RPCLink({
	// Async URL: browser same-origin; server uses request Host (not SITE_URL alone).
	url: () => resolveOrpcRpcUrl(),
	fetch:
		typeof window !== "undefined"
			? browserFetch
			: globalThis.fetch.bind(globalThis),
	headers: async () => {
		if (typeof window !== "undefined") {
			return {};
		}

		const { headers } = await import("next/headers");
		return Object.fromEntries(await headers());
	},
	interceptors: [
		onError((error, options) => {
			// Bind procedure from this call's interceptor options — never a global
			// "last path" (concurrent RPCs overwrite that and mis-label failures).
			logOrpcClientError(error, formatOrpcProcedurePath(options.path));
		}),
	],
});

export const orpcClient: ApiRouterClient = createORPCClient(link);
